import React, { useMemo } from 'react';
import { Box } from '@mui/material';

export type SparkPoint = { time: number; value: number };

interface SensorSparklineProps {
  points: SparkPoint[];
  color?: string;
  width?: number;
  height?: number;
}

const SensorSparkline: React.FC<SensorSparklineProps> = ({
  points,
  color = '#607d8b',
  width = 88,
  height = 28,
}) => {
  const path = useMemo(() => {
    if (points.length < 2) return '';

    // Downsample to keep the path light on dense ranges.
    const maxSamples = 48;
    const step = Math.max(1, Math.floor(points.length / maxSamples));
    const sampled = points.filter((_, i) => i % step === 0 || i === points.length - 1);

    const values = sampled.map((p) => p.value);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const span = max - min || 1;
    const padY = 2;

    return sampled
      .map((p, i) => {
        const x = (i / (sampled.length - 1)) * width;
        const y = padY + (1 - (p.value - min) / span) * (height - padY * 2);
        return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(' ');
  }, [points, width, height]);

  if (points.length < 2) {
    return (
      <Box
        sx={{
          width,
          height,
          display: 'flex',
          alignItems: 'center',
          color: 'text.disabled',
          fontSize: 11,
        }}
      >
        —
      </Box>
    );
  }

  return (
    <Box
      component="svg"
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      sx={{ display: 'block', overflow: 'visible' }}
      aria-hidden
    >
      <path
        d={path}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </Box>
  );
};

export default SensorSparkline;
